import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { createPageUrl } from "./utils";
import { ArrowRight, Menu, X } from "lucide-react";
import { Button } from "./components/ui/button";
import ScrollUpButton from "./components/ScrollUpButton";
import AIChatWidget from "./components/AIChatWidget";

export default function Layout({ children, currentPageName }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigationItems = [
    { name: "Beranda", path: createPageUrl("Home") },
    { name: "Layanan", path: createPageUrl("Services") },
    { name: "Gallery", path: createPageUrl("Gallery") },
    { name: "Kontak", path: createPageUrl("Contact") },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Style langsung di sini */}
      <style>{`
        :root {
          --primary: #2563eb;
          --primary-foreground: #ffffff;
          --secondary: #f8fafc;
          --secondary-foreground: #374151;
          --muted: #f1f5f9;
          --muted-foreground: #64748b;
          --accent: #dbeafe;
          --accent-foreground: #1e40af;
        }
        .smooth-transition { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
        .glass-effect { backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); }
        .fade-in { animation: fadeIn 0.6s ease-out forwards; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .hover-lift:hover { transform: translateY(-2px); }
        .gradient-text { background: linear-gradient(135deg, #2563eb, #1d4ed8); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
        .blue-gradient { background: linear-gradient(135deg, #2563eb, #3b82f6); }
        .blue-glow { box-shadow: 0 0 20px rgba(37, 99, 235, 0.2); }
        @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
  .animate-slideIn {
    animation: slideIn 0.3s ease-out forwards;
  }
      `}</style>

      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 smooth-transition ${
          isScrolled
            ? "bg-white/95 glass-effect shadow-sm border-b border-blue-100"
            : "bg-transparent"
        }`}
      >
        <div className="container mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link
              to={createPageUrl("Home")}
              className="flex items-center gap-3 group"
            >
              <div className="w-12 h-12 rounded-xl overflow-hidden group-hover:scale-110 smooth-transition blue-glow">
                <img
                  src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/57a268115_LOGOGANDIVA.png"
                  alt="Ghandiva Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-bold gradient-text">
                  Ghandiva
                </span>
                <span className="text-xs text-blue-500 -mt-1">
                  IT Solutions
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navigationItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`px-6 py-3 rounded-xl smooth-transition hover-lift ${
                    location.pathname === item.path
                      ? "bg-blue-50 text-blue-600 font-medium"
                      : "text-gray-600 hover:text-blue-600 hover:bg-blue-50/50"
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* CTA Button & Mobile Menu */}
            <div className="flex items-center gap-4">
              <Link to={createPageUrl("Contact")} className="hidden md:block">
                <Button className="blue-gradient hover:shadow-lg text-white px-6 py-2 group smooth-transition hover-lift flex items-center">
  <span>Konsultasi Gratis</span>
  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 smooth-transition" />
</Button>

              </Link>

              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden p-2 rounded-xl hover:bg-blue-50 smooth-transition"
              >
                {isMenuOpen ? (
                  <X className="w-6 h-6 text-gray-700" />
                ) : (
                  <Menu className="w-6 h-6 text-gray-700" />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation Overlay */}
      {isMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-white bg-opacity-95 flex flex-col p-6 animate-slideIn">
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setIsMenuOpen(false)}
              className="p-2 rounded-lg hover:bg-blue-50 smooth-transition"
            >
              <X className="w-6 h-6 text-gray-700" />
            </button>
          </div>

          <nav className="flex flex-col space-y-4">
            {navigationItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => setIsMenuOpen(false)}
                className={`px-4 py-3 rounded-lg text-lg font-medium smooth-transition ${
                  location.pathname === item.path
                    ? "bg-blue-50 text-blue-600"
                    : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                }`}
              >
                {item.name}
              </Link>
            ))}

            <Link
              to={createPageUrl("Contact")}
              onClick={() => setIsMenuOpen(false)}
            >
              <Button className="blue-gradient hover:shadow-lg text-white px-6 py-2 group smooth-transition hover-lift flex items-center">
  <span>Konsultasi Gratis</span>
  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 smooth-transition" />
</Button>

            </Link>
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="fade-in">{children}</main>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-blue-50 to-white border-t border-blue-100">
        <div className="container mx-auto px-6 lg:px-8 py-16 text-center">
          <p className="text-gray-600">
            © 2024 Ghandiva. Semua hak dilindungi undang-undang.
          </p>
        </div>
      </footer>

      {/* Floating Scroll to Top & Tanya AI CS Widget */}
      <ScrollUpButton />
      <AIChatWidget />
    </div>
  );
}
